import { useRef } from 'react'

/**
 * The {@link useReloadRegistry} hook holds a reload callback registered by a
 * nested component (usually a profile tab) and exposes a caller that stays the
 * same regardless of whether that component is currently mounted.
 *
 * @usage
 * ```
 *  const postsTab = useReloadRegistry()
 *
 *  postsTab.handleReload()
 *  <Tab onRegisterReload={postsTab.registerReload} />
 * ```
 *
 * @note
 * The nested component registers itself via {@link useRegisterReload};
 * `handleReload` is a no-op while nothing is registered.
 *
 * @returns {{ handleReload: Function, registerReload: Function }}
 */
export function useReloadRegistry() {
  const reloadRef = useRef(null)

  function registerReload(reload) {
    reloadRef.current = reload
  }

  function handleReload(params) {
    return reloadRef.current?.(params)
  }

  return {
    handleReload,
    registerReload,
  }
}
